import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Modal, Button, Input, Form } from "antd";
import Permission, {
    calcularSelecionarTodosGeralCoerente,
    PERMISSION_MASTER_GROUP_KEYS,
} from "../../components/permission/index";
// import firebase from '../../config/firebase';
import perfilService from "../../service/perfil.service";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import userService from "../../service/user.service";

/** Grupos legados ainda persistidos em perfis antigos, mas fora do checkbox mestre. */
const PERMISSION_LEGACY_GROUP_KEYS = [
    "freightViewEditDriver",
    "freightCannotRelease",
];

/**
 * Chaves de grupo usadas em `components/permission/index.js`.
 * Mantém alinhamento com PERMISSION_MASTER_GROUP_KEYS + chaves legadas.
 */
const PERMISSION_GROUP_KEYS_UI = [
    ...new Set([...PERMISSION_MASTER_GROUP_KEYS, ...PERMISSION_LEGACY_GROUP_KEYS]),
];

/** Grupos exibidos na UI apenas com Visualizar (e Selecionar Todos da linha). */
const VIEW_ONLY_PERMISSION_GROUPS = new Set([
    "salesDashboard",
    "simulatorFreight",
    "revenue",
    "generalReport",
    "accompaniment",
]);

const FLAG_KEYS = ["selecionarTodos", "isView", "isCreate", "isEdit", "isDelete"];

function permissoesCompativelComTela(permissoes) {
    if (!permissoes || typeof permissoes !== "object") return false;
    for (const key of PERMISSION_GROUP_KEYS_UI) {
        if (!(key in permissoes)) return false;
        const grupo = permissoes[key];
        if (!grupo || typeof grupo !== "object") return false;
        for (const flag of FLAG_KEYS) {
            if (!(flag in grupo) || typeof grupo[flag] !== "boolean") {
                return false;
            }
        }
    }
    return true;
}

const MENSAGEM_INCOMPATIBILIDADE_PERMISSOES =
    "Não foi possível carregar as permissões desta tela (configuração desatualizada ou incompleta).\n\n" +
    "Por favor, entre em contato com a equipe de T.I.\n\n" +
    "Você será redirecionado para a listagem de perfis.";

export default function CreatePerfil() {

    const user = useSelector(state => state.user);

    const [loading, setLoading] = useState(false);
    const [update, setUpdate] = useState(false);
    const [record, setRecord] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();

    const grupoPermissaoVazio = () => ({
        selecionarTodos: false,
        isView: false,
        isCreate: false,
        isEdit: false,
        isDelete: false,
    });

    const initialPermissoes = PERMISSION_GROUP_KEYS_UI.reduce((acc, key) => {
        acc[key] = grupoPermissaoVazio();
        return acc;
    }, {});

    if (process.env.NODE_ENV === "development") {
        const faltandoEmInitial = PERMISSION_GROUP_KEYS_UI.filter(
            (k) => !(k in initialPermissoes),
        );
        if (faltandoEmInitial.length > 0) {
            // eslint-disable-next-line no-console
            console.error(
                "[CreatePerfil] Grupos em PERMISSION_GROUP_KEYS_UI sem entrada em initialPermissoes:",
                faltandoEmInitial,
            );
        }
    }

    const mergePermissoesComPadrao = (incoming) => {
        if (!incoming) return { ...initialPermissoes };
        const merged = { ...initialPermissoes };
        Object.keys(merged).forEach((key) => {
            merged[key] = {
                ...merged[key],
                ...(incoming[key] || {}),
            };
        });
        Object.keys(incoming).forEach((key) => {
            if (!(key in merged)) {
                merged[key] = {
                    ...grupoPermissaoVazio(),
                    ...(incoming[key] || {}),
                };
            }
        });
        return merged;
    };

    const [permissoes, setPermissoes] = useState(() =>
        mergePermissoesComPadrao(location.state?.permissoes),
    );
    const [view, setView] = useState(location.state?.viewOnly);

    const [selecionarTodosGeral, setSelecionarTodosGeral] = useState(() =>
        calcularSelecionarTodosGeralCoerente(
            mergePermissoesComPadrao(location.state?.permissoes),
        ),
    );

    const [form] = Form.useForm();

    const redirecionouPorIncompatibilidade = useRef(false);

    const redirecionarSePermissoesInvalidas = (merged) => {
        if (redirecionouPorIncompatibilidade.current) return true;
        if (permissoesCompativelComTela(merged)) return false;
        redirecionouPorIncompatibilidade.current = true;
        alert(MENSAGEM_INCOMPATIBILIDADE_PERMISSOES);
        navigate("/listPerfil", { replace: true });
        return true;
    };

    useLayoutEffect(() => {
        redirecionarSePermissoesInvalidas(permissoes);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!location.state || !location.state.record) return;
        const record = location.state.record;
        const merged = mergePermissoesComPadrao(record.permissoes);
        if (redirecionarSePermissoesInvalidas(merged)) return;
        form.setFieldsValue({
            nomePerfil: record.nomePerfil,
        });
        setPermissoes(merged);
        setSelecionarTodosGeral(calcularSelecionarTodosGeralCoerente(merged));
        setRecord(record);
        setUpdate(true);
    }, [location.state]);

    const recalcularSelecionarTodosGrupo = (grupo, grupoPermissao) => {
        if (VIEW_ONLY_PERMISSION_GROUPS.has(grupo)) {
            return grupoPermissao.isView === true;
        }
        return (
            grupoPermissao.isView &&
            grupoPermissao.isCreate &&
            grupoPermissao.isEdit &&
            grupoPermissao.isDelete
        );
    };

    const handleChange = (grupo, campo) => {
        setPermissoes((prev) => {
            const atual = prev[grupo] || grupoPermissaoVazio();
            let novoGrupo;

            if (campo === "selecionarTodos") {
                const novoValor = !atual.selecionarTodos;
                if (VIEW_ONLY_PERMISSION_GROUPS.has(grupo)) {
                    novoGrupo = {
                        selecionarTodos: novoValor,
                        isView: novoValor,
                        isCreate: false,
                        isEdit: false,
                        isDelete: false,
                    };
                } else {
                    novoGrupo = {
                        selecionarTodos: novoValor,
                        isView: novoValor,
                        isCreate: novoValor,
                        isEdit: novoValor,
                        isDelete: novoValor,
                    };
                }
            } else {
                novoGrupo = {
                    ...atual,
                    [campo]: !atual[campo],
                };
                novoGrupo.selecionarTodos = recalcularSelecionarTodosGrupo(grupo, novoGrupo);
            }

            const novoEstado = {
                ...prev,
                [grupo]: novoGrupo,
            };

            setSelecionarTodosGeral(calcularSelecionarTodosGeralCoerente(novoEstado));

            return novoEstado;
        });
    };

    // handler geral
    const handleSelecionarTodosGeral = () => {
        setPermissoes((prev) => {
            const novoValor = !calcularSelecionarTodosGeralCoerente(prev);
            const novoEstado = {};

            for (const grupo of PERMISSION_GROUP_KEYS_UI) {
                if (VIEW_ONLY_PERMISSION_GROUPS.has(grupo)) {
                    novoEstado[grupo] = {
                        selecionarTodos: novoValor,
                        isView: novoValor,
                        isCreate: false,
                        isEdit: false,
                        isDelete: false,
                    };
                } else {
                    novoEstado[grupo] = {
                        selecionarTodos: novoValor,
                        isView: novoValor,
                        isCreate: novoValor,
                        isEdit: novoValor,
                        isDelete: novoValor,
                    };
                }
            }

            setSelecionarTodosGeral(novoValor);

            return novoEstado;
        });
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {

            const list = await perfilService.getPerfis();

            if (!update && list.some((perfil) => perfil.nomePerfil.toLowerCase() === values.nomePerfil.toLowerCase())) {
                alert("Já existe um perfil com esse nome. Por favor, escolha outro nome.");
                setLoading(false);
                return;
            }

            if (!update) {  
                await perfilService.savePerfil({
                    nomePerfil: values.nomePerfil,
                    permissoes: permissoes,
                    selecionarTodosGeral:
                        calcularSelecionarTodosGeralCoerente(permissoes),
                    uidShipper: user.uidShipper || "uid não encontrado",
                });
                alert("Perfil criado com sucesso!");
                navigate("/listPerfil");
            }
            else {
                await perfilService.updatePerfil(record.id, {
                    nomePerfil: values.nomePerfil,
                    permissoes: permissoes,
                    selecionarTodosGeral:
                        calcularSelecionarTodosGeralCoerente(permissoes),
                    uidShipper: user.uidShipper || "uid não encontrado",
                });
                await userService.updateUserPerfil(
                    values.nomePerfil,
                    permissoes,
                    calcularSelecionarTodosGeralCoerente(permissoes),
                );
                alert("Perfil atualizado com sucesso!"); //TODO alterar para modal de menssagem
                navigate("/listPerfil");
            }
        } 
        catch (error) {
            console.error("Erro ao salvar perfil:", error);
            alert("Houve um erro ao salvar esse perfil!");
        }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (location.state && location.state.record) {
            const record = location.state.record;
            form.setFieldsValue({
                nomePerfil: record.nomePerfil,
            });
            const mergedPermissoes = { ...initialPermissoes };
            Object.keys(initialPermissoes).forEach((key) => {
                mergedPermissoes[key] = {
                    ...initialPermissoes[key],
                    ...(record.permissoes?.[key] || {}),
                };
            });
            Object.keys(record.permissoes || {}).forEach((group) => {
                if (!mergedPermissoes[group]) {
                    mergedPermissoes[group] = {
                        ...grupoPermissaoVazio(),
                        ...record.permissoes[group],
                    };
                }
            });
            setPermissoes(mergedPermissoes);
            setSelecionarTodosGeral(record.selecionarTodosGeral);
            setRecord(record);
            setUpdate(true);
        }
    }, [location.state]);

    return (
        <>
            <NewMiniDrawer divOpen={
                <>
                    <div className="container mb-3">
                        <div className="row">
                            <div className="col-5">
                                {
                                    update ? <h4>Editar Perfil</h4> : <h4>Cadastrar Perfil</h4>
                                }
                            </div>
                        </div>
                    </div>
                    <Form style={{ padding: "0 100px", margin: 0, justifyContent: "flex-start" }} form={form} layout="vertical" onFinish={onFinish} >
                        <Form.Item
                            className="Form"
                            name="nomePerfil"
                            label="Nome do Perfil"
                            rules={[{ required: true, message: "Informe o nome do perfil" }]}
                        >
                            <Input disabled={view} placeholder="Digite o nome do perfil" />
                        </Form.Item>

                        <Form.Item className="Form" label="Permissões" name="permissions">
                            <Permission
                                view={view}
                                permissoes={permissoes}
                                handleChange={handleChange}
                                handleSelecionarTodosGeral={handleSelecionarTodosGeral}
                            />
                        </Form.Item>
                        {/* Botões fora do modal */}
                        <div style={{ display: "flex", justifyContent: "end", gap: "10px" }}>
                            <Button onClick={() => {form.resetFields(); navigate("/listPerfil")}}>Cancelar</Button>
                            <Button disabled={view} type="primary" htmlType="submit" loading={loading}>
                                {
                                    update ? "Atualizar" : "Salvar"
                                }
                            </Button>
                        </div>
                    </Form>
                </>
            } />
        </>
    )

}